import { Image, View } from "react-native";
import { Plus } from "lucide-react-native";
import { DialogHeader, SecondaryButton, DocumentRow } from "@louise/moon-ui";
import { ThinkingOrbStatus } from "@src/components/ui/ThinkingOrbStatus";
import type { ThinkingOrbState } from "@src/components/ui/ThinkingOrbStatus.types";
import { useTranslations } from "@src/packages/i18n";
import { semanticColors } from "@src/packages/moon-ui/theme/tokens";

const uploadFileIcons = {
  document:
    require("@src/apps/moon/assets/images/upload-file-icons/document-3d.png") as number,
  image:
    require("@src/apps/moon/assets/images/upload-file-icons/image-3d.png") as number,
  generic:
    require("@src/apps/moon/assets/images/upload-file-icons/generic-3d.png") as number,
} as const;

export type DocumentUploadProcessingPhase = "queued" | "uploading" | "reading";

export type DocumentUploadProcessingItem = {
  id: string;
  name: string;
  detail: string;
  phase: DocumentUploadProcessingPhase;
};

type Props = {
  phase: Exclude<DocumentUploadProcessingPhase, "queued">;
  items: DocumentUploadProcessingItem[];
  onAddMoreFiles: () => void;
};

function fileIconKind(fileName: string): keyof typeof uploadFileIcons {
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (
    extension &&
    ["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(extension)
  ) {
    return "image";
  }
  if (
    extension &&
    ["pdf", "doc", "docx", "txt", "csv", "xls", "xlsx"].includes(extension)
  ) {
    return "document";
  }
  return "generic";
}

export function DocumentFileTypeIcon({ fileName }: { fileName: string }) {
  return (
    <Image
      accessibilityLabel={`${fileIconKind(fileName)} file`}
      source={uploadFileIcons[fileIconKind(fileName)]}
      resizeMode="contain"
      style={{ height: 34, width: 34 }}
    />
  );
}

function phaseOrbState(phase: DocumentUploadProcessingPhase): ThinkingOrbState {
  switch (phase) {
    case "uploading":
      return "composing";
    case "reading":
      return "shaping";
    case "queued":
      return "listening";
  }
}

const phaseLabelKeys: Record<
  DocumentUploadProcessingPhase,
  | "documents.upload.processing_uploading"
  | "documents.upload.processing_reading"
  | "documents.upload.processing_next_up"
> = {
  uploading: "documents.upload.processing_uploading",
  reading: "documents.upload.processing_reading",
  queued: "documents.upload.processing_next_up",
};

function ProcessingItemRow({ item }: { item: DocumentUploadProcessingItem }) {
  const t = useTranslations();
  const phaseLabel = t(phaseLabelKeys[item.phase]);

  return (
    <DocumentRow
      accessibilityLabel={`${item.name}, ${item.detail}`}
      title={item.name}
      subtitle={item.detail}
      leading={<DocumentFileTypeIcon fileName={item.name} />}
      trailing={
        <ThinkingOrbStatus
          accessibilityLabel={`${phaseLabel} ${item.name}`}
          label={phaseLabel}
          state={phaseOrbState(item.phase)}
          size={20}
        />
      }
      showChevron={false}
      titleNumberOfLines={1}
      subtitleNumberOfLines={1}
      style={{ paddingVertical: 9 }}
    />
  );
}

export function DocumentUploadProcessingStep({
  phase,
  items,
  onAddMoreFiles,
}: Props) {
  const t = useTranslations();
  const isReading = phase === "reading";
  const title = t(
    isReading
      ? "documents.upload.reading_document_title"
      : "documents.upload.uploading_document_title",
  );
  const subtitle = t(
    isReading
      ? "documents.upload.reading_document_subtitle"
      : "documents.upload.uploading_document_subtitle",
  );

  return (
    <View style={{ gap: 18 }} testID={`document-upload-${phase}`}>
      <View style={{ alignItems: "center", gap: 2 }}>
        <ThinkingOrbStatus
          accessibilityLabel={title}
          state={isReading ? "shaping" : "working"}
          variant="hero"
          size={64}
        />
        <DialogHeader
          align="center"
          title={`${title}…`}
          subtitle={subtitle}
          style={{ alignItems: "center", width: "100%" }}
          subtitleStyle={{ marginTop: 0, textAlign: "center" }}
        />
      </View>

      <View style={{ gap: 9 }}>
        {items.map((item) => (
          <ProcessingItemRow key={item.id} item={item} />
        ))}
      </View>

      <SecondaryButton
        label={t("documents.upload.add_more_files")}
        onPress={onAddMoreFiles}
        leading={
          <Plus
            size={16}
            color={semanticColors.text.secondary}
            strokeWidth={2}
          />
        }
      />
    </View>
  );
}
